import { useState, type ReactNode } from 'react'
import { ConfirmDialog } from './ConfirmDialog'

interface ConfirmOpts {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmTone?: 'gold' | 'accent' | 'neutral' | 'danger'
  /** One-button styled alert (resolves true on the single action) */
  hideCancel?: boolean
  icon?: ReactNode
}

interface Pending extends ConfirmOpts {
  resolve: (value: boolean) => void
}

/**
 * Promise-based, on-brand replacement for window.confirm()/alert(). Returns a
 * `confirm(opts)` that resolves true/false, and the `confirmDialog` node to
 * render once in the component. Near drop-in: `if (await confirm({...})) {...}`.
 * For an alert, pass `hideCancel: true` and ignore the result.
 */
export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null)

  const confirm = (opts: ConfirmOpts) =>
    new Promise<boolean>(resolve => setPending({ ...opts, resolve }))

  const settle = (value: boolean) => {
    pending?.resolve(value)
    setPending(null)
  }

  const confirmDialog = (
    <ConfirmDialog
      open={!!pending}
      title={pending?.title ?? ''}
      message={pending?.message}
      confirmLabel={pending?.confirmLabel}
      cancelLabel={pending?.cancelLabel}
      confirmTone={pending?.confirmTone}
      hideCancel={pending?.hideCancel}
      icon={pending?.icon}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  )

  return { confirm, confirmDialog }
}
